import { GetCampaignResponse } from '@types';
import CancelOutlined from '@icons/CancelOutlined';
import CheckCircleOutlined from '@icons/CheckCircleOutlined';
import PauseFilled from '@icons/PauseFilled';
import RunFilterOutlined from '@icons/RunFilterOutlined';
import RunSaveOutlined from '@icons/RunSaveOutlined';
import RunTimeNoArrowOutlined from '@icons/RunTimeNoArrowOutlined';
import SandTimerOutlined from '@icons/SandTimerOutlined';
import WarningOutlined from '@icons/WarningOutlined';

export type StatusEntry = { icon: React.ElementType; color: string; label: string };

export const CAMPAIGN_STATUS_MAP: Record<string, StatusEntry> = {
  QUEUED: { icon: SandTimerOutlined, color: 'secondary.dark', label: 'Queued' },
  RUNNING: { icon: RunTimeNoArrowOutlined, color: 'info.dark', label: 'Running' },
  PROCESS_ROWBOAT: { icon: RunFilterOutlined, color: 'info.dark', label: 'Processing' },
  WARM_ROWBOAT: { icon: RunSaveOutlined, color: 'info.dark', label: 'Warming' },
  ACTIVE: { icon: CheckCircleOutlined, color: 'success.main', label: 'Active' },
  FAILED: { icon: CancelOutlined, color: 'error.dark', label: 'Failed' },
  FAILED_ADMIN: { icon: WarningOutlined, color: 'error.dark', label: 'Failed (admin)' },
  FAILED_PERMANENTLY: { icon: WarningOutlined, color: 'error.dark', label: 'Failed permanently' },
  DEFAULT: { icon: SandTimerOutlined, color: 'action.disabled', label: 'Pending' },
  DISABLED: { icon: PauseFilled, color: 'action.active', label: 'Disabled' }
};

export function getCampaignStatusEntry(campaign: Pick<GetCampaignResponse, 'enabled' | 'status' | 'last_run'>): StatusEntry {
  const statusKey = campaign.status?.toUpperCase();

  if (statusKey === 'QUEUED' && campaign.last_run) {
    return CAMPAIGN_STATUS_MAP['ACTIVE'];
  }

  return CAMPAIGN_STATUS_MAP[statusKey] ?? CAMPAIGN_STATUS_MAP['DEFAULT'];
}

type WarmFields = Partial<Pick<GetCampaignResponse, 'status' | 'record_count' | 'last_run' | 'warming_identifier' | 'warming_completed'>>;

/** The warm is still writing months (newest first) into the campaign's table. */
export function isCampaignWarming(campaign: Pick<WarmFields, 'status'>): boolean {
  return campaign.status?.toUpperCase() === 'WARM_ROWBOAT';
}

/**
 * At least one month with data is committed while the warm continues: the warm publishes
 * record_count, min/max_record_date and bounds after each month, so the data covers
 * min_record_date to max_record_date so far.
 */
export function isCampaignPartiallyWarm(campaign: Pick<WarmFields, 'status' | 'record_count'>): boolean {
  return isCampaignWarming(campaign) && campaign.record_count != null;
}

/** The campaign can be opened: its warm finished, or its first month is committed. */
export function isCampaignViewable(campaign: Pick<WarmFields, 'status' | 'record_count' | 'last_run'>): boolean {
  return Boolean(campaign.last_run) || isCampaignPartiallyWarm(campaign);
}

/** The map can read the campaign from its warmed table (/ege/cache) rather than /ege/. */
export function isCampaignDataWarm(campaign: WarmFields | null | undefined): boolean {
  if (!campaign?.warming_identifier) return false;
  return Boolean(campaign.warming_completed) || isCampaignPartiallyWarm(campaign);
}
